package com.anjuros.website.templates.template9.styles

import com.anjuros.website.templates.template9.modules.Template9Theme
import com.varabyte.kobweb.compose.css.StyleVariable
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.boxShadow
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.lineHeight
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.scale
import com.varabyte.kobweb.compose.ui.modifiers.size
import com.varabyte.kobweb.compose.ui.modifiers.transition
import com.varabyte.kobweb.compose.ui.modifiers.translateY
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.selectors.hover
import org.jetbrains.compose.web.css.CSSColorValue
import org.jetbrains.compose.web.css.ms
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.rgba


val Template9ColorVar by StyleVariable<CSSColorValue>()

// Organic "blob" shapes used for the photos (CSS border-radius with 8 values)
const val TEMPLATE9_BLOB_HERO = "62% 38% 46% 54% / 52% 44% 56% 48%"
const val TEMPLATE9_BLOB_ABOUT = "48% 52% 42% 58% / 56% 42% 58% 44%"
const val TEMPLATE9_BLOB_WHY = "44% 56% 52% 48% / 40% 46% 54% 60%"
const val TEMPLATE9_BLOB_CTA = "55% 45% 50% 50% / 50% 55% 45% 50%"
const val TEMPLATE9_ARCH = "50% 50% 0 0 / 38% 38% 0 0"

fun Modifier.template9Blob(shape: String): Modifier = this.styleModifier {
    property("border-radius", shape)
}

val Template9ContainerStyle = CssStyle {
    base { Modifier.fillMaxWidth(90.percent).maxWidth(1180.px) }
    Breakpoint.LG { Modifier.fillMaxWidth(88.percent).maxWidth(1180.px) }
}

val Template9SectionPaddingStyle = CssStyle {
    base { Modifier.padding(topBottom = 60.px) }
    Breakpoint.MD { Modifier.padding(topBottom = 80.px) }
    Breakpoint.LG { Modifier.padding(topBottom = 90.px) }
}

val Template9NavigationItemStyle = CssStyle {
    base {
        Modifier
            .color(Template9Theme.Secondary.rgb)
            .transition(Transition.of(property = "color", duration = 200.ms))
    }
    hover {
        Modifier.color(Template9ColorVar.value())
    }
}

val Template9MenuItemStyle = CssStyle {
    base {
        Modifier
            .color(Template9Theme.Secondary.rgb)
            .transition(Transition.of(property = "color", duration = 200.ms))
    }
    hover {
        Modifier.color(Template9ColorVar.value())
    }
}

val Template9FooterLinkStyle = CssStyle {
    base {
        Modifier
            .color(Colors.White)
            .transition(Transition.of(property = "color", duration = 200.ms))
    }
    hover {
        Modifier.color(Template9Theme.Accent.rgb)
    }
}

val Template9ButtonStyle = CssStyle {
    base {
        Modifier
            .translateY(0.px)
            .styleModifier {
                property("transition", "translate 250ms ease, box-shadow 250ms ease")
            }
    }
    hover {
        Modifier
            .translateY((-3).px)
            .boxShadow(offsetY = 8.px, blurRadius = 18.px, color = rgba(27, 33, 66, 0.22))
    }
}

val Template9CardStyle = CssStyle {
    base {
        Modifier
            .translateY(0.px)
            .boxShadow(offsetY = 4.px, blurRadius = 14.px, color = rgba(27, 33, 66, 0.06))
            .styleModifier {
                property("transition", "translate 300ms ease, box-shadow 300ms ease")
            }
    }
    hover {
        Modifier
            .translateY((-6).px)
            .boxShadow(offsetY = 14.px, blurRadius = 28.px, color = rgba(27, 33, 66, 0.14))
    }
}

val Template9ImageZoomStyle = CssStyle {
    base {
        Modifier
            .scale(1.0)
            .transition(Transition.of(property = "scale", duration = 600.ms))
    }
    hover {
        Modifier.scale(1.04)
    }
}

val Template9HeroTitleStyle = CssStyle {
    base { Modifier.fontSize(46.px).lineHeight(1.05) }
    Breakpoint.MD { Modifier.fontSize(60.px).lineHeight(1.05) }
    Breakpoint.LG { Modifier.fontSize(72.px).lineHeight(1.05) }
}

val Template9HeroSubtitleStyle = CssStyle {
    base { Modifier.fontSize(22.px).lineHeight(1.35) }
    Breakpoint.MD { Modifier.fontSize(26.px).lineHeight(1.35) }
    Breakpoint.LG { Modifier.fontSize(28.px).lineHeight(1.35) }
}

val Template9SectionTitleStyle = CssStyle {
    base { Modifier.fontSize(32.px).lineHeight(1.15) }
    Breakpoint.MD { Modifier.fontSize(38.px).lineHeight(1.15) }
    Breakpoint.LG { Modifier.fontSize(42.px).lineHeight(1.15) }
}

val Template9HeroImageStyle = CssStyle {
    base { Modifier.size(310.px, 300.px).margin(top = 40.px) }
    Breakpoint.MD { Modifier.size(440.px, 420.px).margin(top = 40.px) }
    Breakpoint.LG { Modifier.size(500.px, 470.px).margin(top = 0.px) }
}

val Template9AboutImageBoxStyle = CssStyle {
    base { Modifier.size(320.px, 340.px) }
    Breakpoint.MD { Modifier.size(420.px, 430.px) }
    Breakpoint.LG { Modifier.size(460.px, 460.px) }
}

val Template9AboutSmallImageStyle = CssStyle {
    base { Modifier.size(120.px) }
    Breakpoint.MD { Modifier.size(160.px) }
    Breakpoint.LG { Modifier.size(170.px) }
}

val Template9WhyImageStyle = CssStyle {
    base { Modifier.size(290.px, 300.px).margin(top = 40.px) }
    Breakpoint.MD { Modifier.size(380.px, 380.px).margin(top = 40.px) }
    Breakpoint.LG { Modifier.size(420.px, 420.px).margin(top = 0.px) }
}

val Template9CTAImageStyle = CssStyle {
    base { Modifier.size(150.px) }
    Breakpoint.LG { Modifier.size(190.px) }
}

val Template9MenuWidthStyle = CssStyle {
    base { Modifier.width(75.percent) }
    Breakpoint.SM { Modifier.width(50.percent) }
    Breakpoint.MD { Modifier.width(35.percent) }
}

val Template9WaveStyle = CssStyle {
    base { Modifier.height(40.px) }
    Breakpoint.MD { Modifier.height(60.px) }
    Breakpoint.LG { Modifier.height(80.px) }
}

val Template9FormWidthStyle = CssStyle {
    base { Modifier.fillMaxWidth(100.percent) }
    Breakpoint.MD { Modifier.fillMaxWidth(80.percent) }
    Breakpoint.LG { Modifier.fillMaxWidth(60.percent) }
}
